import { resolveWhen } from "@segment/analytics-consent-tools";
import { variation } from "../services/launchDarkly";
import { analytics } from "./analytics";
import * as amplitude from "@amplitude/unified";

let amplitudeInitialized = false;

/**
 * Gets the current DataGrail consent preferences
 * @returns {Object} Consent preferences object with category keys
 */
const getConsentPreferences = () => {
  if (typeof window === "undefined") {
    return {};
  }

  if (window.__segmentCategoryPreferences) {
    return window.__segmentCategoryPreferences;
  }

  // Fallback to checking DataGrail API directly
  if (window.DG_BANNER_API) {
    try {
      const categories = window.DG_BANNER_API.getCategories();
      if (Array.isArray(categories)) {
        const preferences = {};
        categories.forEach((opt) => {
          if (opt.gtm_key === "dg-category-performance" && opt.isEnabled) {
            preferences.Analytics = true;
          }
        });
        return preferences;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error getting DataGrail consent categories for Amplitude", error);
    }
  }

  return {};
};

/**
 * Checks if Session Replay consent is enabled (same category as FullStory)
 * @returns {boolean} True if Session Replay consent is enabled
 */
const hasSessionReplayConsent = () => {
  const preferences = getConsentPreferences();
  // Session Replay uses the same consent category as FullStory (Analytics/Session Replay)
  return preferences["Session Replay"] === true || preferences.Analytics === true;
};

/**
 * Waits for DataGrail consent API to be available and checks Session Replay consent
 * @returns {Promise<boolean>} True if Session Replay consent is granted
 */
const waitForSessionReplayConsent = async () => {
  if (typeof window === "undefined") {
    return false;
  }

  await resolveWhen(() => window.DG_BANNER_API !== undefined, 500);

  if (!window.DG_BANNER_API) {
    return false;
  }

  try {
    const config = window.DG_BANNER_API.config();
    const consentModel = config.consentMode;

    // For opt-out model, consent is granted by default
    if (consentModel === "optout") {
      return hasSessionReplayConsent();
    }

    // For opt-in model, wait for banner to be dismissed
    if (consentModel === "optin") {
      const categories = window.DG_BANNER_API.getCategories();
      await resolveWhen(() => !config.showBanner && categories.length > 0, 500);
      return hasSessionReplayConsent();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error checking DataGrail consent for Amplitude Session Replay", error);
    return false;
  }

  return false;
};

/**
 * Initializes Amplitude Unified SDK with Session Replay and Segment integration
 * Uses the same consent category as FullStory (Session Replay/Analytics)
 * Integrates with Segment's Amplitude Actions destination
 * @returns {Promise<void>}
 */
export const initializeAmplitude = async () => {
  if (amplitudeInitialized) {
    return;
  }

  const amplitudeEnabled = variation("amplitude-integration", false);
  if (!amplitudeEnabled) {
    return;
  }

  const apiKey = process.env.GATSBY_AMPLITUDE_API_KEY;
  if (!apiKey) {
    return;
  }

  try {
    const hasConsent = await waitForSessionReplayConsent();

    if (!hasConsent) {
      return;
    }

    const sampleRate =
      parseFloat(process.env.GATSBY_AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE) || 0.7;

    const userInfo = await analytics.waitForUserInfo();
    const { id: userId, anonymousId: deviceId } = userInfo;
    const amplitudeConfig = {
      serverZone: "US",
      analytics: {
        userId,
        deviceId,
        autocapture: {
          formInteractions: true,
          frustrationInteractions: true
        },
      },
      sessionReplay: {
        deviceId,
        sampleRate,
        optOut: false
      },
    };

    amplitude.initAll(apiKey, amplitudeConfig);

    amplitudeInitialized = true;

    // Note: Once Amplitude is initialized, it cannot be uninitialized.
    // If consent is revoked later, Amplitude will continue to track,
    // but this is acceptable as the SDK was only loaded after consent was granted.
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error initializing Amplitude Unified", error);
  }
};
